/**
 * Client error reporter (Feb 2026)
 *
 * Mirrors the soul_circle backend error logger: catches global JS errors
 * (window.error, unhandledrejection) and React boundary crashes and POSTs
 * them to /api/client-errors. Failures are silent — never break the app
 * trying to report an error.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory dedupe — don't spam the same error every render
const _recent = new Set();
const _RECENT_LIMIT = 30;

function _shouldReport(message) {
  if (!message) return false;
  // Drop noisy benign errors that don't need our attention
  const noise = [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Non-Error promise rejection captured",
    "Script error.",
  ];
  if (noise.some((n) => message.includes(n))) return false;

  if (_recent.has(message)) return false;
  _recent.add(message);
  if (_recent.size > _RECENT_LIMIT) {
    const first = _recent.values().next().value;
    _recent.delete(first);
  }
  return true;
}

export async function reportClientError(payload) {
  try {
    const message = payload?.message || "Unknown error";
    if (!_shouldReport(message)) return;

    const body = {
      message: String(message).slice(0, 2000),
      stack: payload?.stack ? String(payload.stack).slice(0, 8000) : null,
      component_stack: payload?.componentStack
        ? String(payload.componentStack).slice(0, 4000)
        : null,
      url: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      severity: payload?.severity || "error",
    };
    const headers = { "Content-Type": "application/json" };
    const token = localStorage.getItem("token");
    if (token) headers.Authorization = `Bearer ${token}`;

    // navigator.sendBeacon is the reliable choice for "page-unload" cases,
    // but it doesn't allow custom headers. Use it only when no token.
    if (!token && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(`${API}/client-errors`, blob);
      return;
    }

    await fetch(`${API}/client-errors`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      // Don't keep the user waiting on this
      keepalive: true,
    });
  } catch {
    // Reporter must never throw — swallow silently
  }
}

let _attached = false;

/** Wire up the global error handlers exactly once on app boot. */
export function attachGlobalErrorReporter() {
  if (_attached || typeof window === "undefined") return;
  _attached = true;

  window.addEventListener("error", (event) => {
    reportClientError({
      message: event?.message || "window.error",
      stack: event?.error?.stack,
      severity: "error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const message =
      typeof reason === "string"
        ? reason
        : reason?.message || "Unhandled promise rejection";
    reportClientError({
      message,
      stack: reason?.stack,
      severity: "error",
    });
  });
}
